import { useEffect } from "react";
import { numToCurrency, decimToPercent } from "../utils/transformators";
import * as Calculations from "../utils/calculations";
import useUSQS from "../../../hooks/useUSQS";
import moment from "moment";

const useCalculations = () => {
  // Load context values
  const { USQSData, setUSQS, setUSQSData, ppaymenttermDataUSQS, spaymenttermDataUSQS, whdata, setWhdata, inEuros } =
    useUSQS();
  useEffect(() => {
    const docalcs = async () => {
      // totalinboundcalc
      const ttloutbound = await Calculations.sumvalues(
        USQSData.outdrayage,
        USQSData.loading,
        USQSData.outcollectcharges,
        USQSData.outboundothers
      );
      // outboundpmtcalc
      const outbpmt = await Calculations.dividevalues(ttloutbound, USQSData.outpayload);
      // freightpmtcalc
      const frtpmt = await Calculations.dividevalues(USQSData.oceanfreight, USQSData.oceanpayload);
      //  materialvaluecalc
      const matvalue = await Calculations.multiplyvalues(USQSData.quantity, USQSData.materialcost);
      // storagecost
      let stgcost;
      if (USQSData.qstype == 2) {
        const daysInStorage = Math.max(
          moment(USQSData.whexit, "YYYY-MM-DD").diff(moment(whdata?.whentrydate, "YYYY-MM-DD"), "days") -
            whdata?.whgraceperiod,
          0
        );
        setWhdata((prev) => prev && { ...prev, ["daysinstorage"]: daysInStorage });
        stgcost = await Calculations.storagepmtcalc(
          daysInStorage,
          whdata?.whfixedcost,
          whdata?.whvarcost,
          whdata?.quantity,
          whdata?.numpallets,
          whdata?.whaccrualperiod
        );
        // console.log(stgcost);
      } else {
        stgcost = 0;
      }
      // totaldutycalc
      const ttlduty = await Calculations.sumvalues(USQSData.generaldutyimp, USQSData.additionaldutyimp);
      // dutyfeecalc
      const dtyfee = await Calculations.multiplyvalues(matvalue, ttlduty);
      // harborfeecalc
      const hbrfee = await Calculations.multiplyvalues(matvalue, USQSData.harborfeepct);
      // merchprocfeecalc
      const mrcfee = await Calculations.multiplyvalues(matvalue, USQSData.merchprocfeepct);
      // totalcentryfeecalc
      const ttlcentryfee = await Calculations.sumvalues(
        dtyfee,
        hbrfee,
        mrcfee,
        USQSData.flatfee,
        USQSData.tsca,
        USQSData.isf
      );
      // centryfeepmtcalc
      const centrypmt = await Calculations.dividevalues(ttlcentryfee, USQSData.quantity);
      // totalinboundcalc
      const ttlinbound = await Calculations.sumvalues(
        USQSData.indrayage,
        USQSData.unloading,
        USQSData.incollectcharges,
        USQSData.inboundothers
      );
      // inboundpmtcalc
      const inbpmt = await Calculations.dividevalues(ttlinbound, USQSData.inpayload);
      // totalcostcalc
      const ttlcost = await Calculations.sumvalues(
        USQSData.materialcost,
        stgcost || 0,
        outbpmt,
        centrypmt,
        inbpmt,
        USQSData.pcomission,
        USQSData.pfinancecost,
        USQSData.scomission,
        USQSData.sfinancecost,
        frtpmt,
        USQSData.inspectioncost,
        USQSData.legal,
        USQSData.pallets,
        USQSData.other
      );
      // insurcalc
      const inscost = await Calculations.multiplyvalues(ttlcost, USQSData.insurancerate, USQSData.insurancefactor);
      // interestcostcalc
      const intercost = await Calculations.interestcostcalc(
        ttlcost,
        ppaymenttermDataUSQS,
        spaymenttermDataUSQS,
        USQSData.pinterest,
        USQSData.sinterest,
        USQSData.salesprice
      );
      // finalpricecalc
      const paii = await Calculations.sumvalues(ttlcost, intercost, inscost);
      // profitcalc
      const profit = USQSData.quantity && (await Calculations.subvalues(USQSData.salesprice, paii));
      // totalprofitcalc
      const totalprofit = profit * USQSData.quantity;
      // turnovercalc
      const trnover = USQSData.quantity && (await Calculations.multiplyvalues(USQSData.salesprice, USQSData.quantity));
      // margincalc
      const mrgin = trnover > 0 ? await Calculations.dividevalues(totalprofit, trnover) : 0;
      // netbackcalc
      const ntback = await Calculations.sumvalues(profit, USQSData.materialcost);

      const symbol = inEuros ? "€" : "$";
      const ERate = USQSData.exchangeRateUsdToEur;

      setUSQS((prev) => ({
        ...prev,
        totaloutbound: numToCurrency(inEuros ? ttloutbound * ERate : ttloutbound, 2, symbol),
        outboundcost: numToCurrency(inEuros ? outbpmt * ERate : outbpmt, 2, symbol),
        freightpmt: numToCurrency(inEuros ? frtpmt * ERate : frtpmt, 2, symbol),
        materialvalue: numToCurrency(inEuros ? matvalue * ERate : matvalue, 2, symbol),
        totalduty: decimToPercent(ttlduty),
        dutyfee: numToCurrency(inEuros ? dtyfee * ERate : dtyfee, 2, symbol),
        harborfee: numToCurrency(inEuros ? hbrfee * ERate : hbrfee, 2, symbol),
        merchprocfee: numToCurrency(inEuros ? mrcfee * ERate : mrcfee, 2, symbol),
        totalcentryfee: numToCurrency(inEuros ? ttlcentryfee * ERate : ttlcentryfee, 2, symbol),
        importcustomsfee: numToCurrency(inEuros ? centrypmt * ERate : centrypmt, 2, symbol),
        totalinbound: numToCurrency(inEuros ? ttlinbound * ERate : ttlinbound, 2, symbol),
        inboundcost: numToCurrency(inEuros ? inbpmt * ERate : inbpmt, 2, symbol),
        totalcost: numToCurrency(inEuros ? ttlcost * ERate : ttlcost, 2, symbol),
        insurancecost: numToCurrency(inEuros ? inscost * ERate : inscost, 2, symbol),
        interestcost: numToCurrency(inEuros ? intercost * ERate : intercost, 2, symbol),
        totallandedcost: numToCurrency(inEuros ? paii * ERate : paii, 2, symbol),
        profitpmt: numToCurrency(inEuros ? profit * ERate : profit, 2, symbol),
        profittotal: numToCurrency(inEuros ? totalprofit * ERate : totalprofit, 2, symbol),
        turnover: numToCurrency(inEuros ? trnover * ERate : trnover, 2, symbol),
        margin: decimToPercent(mrgin),
        netback: numToCurrency(inEuros ? ntback * ERate : ntback, 2, symbol),
        storagecost: numToCurrency(inEuros ? stgcost * ERate : stgcost, 2, symbol),
      }));
      setUSQSData((prev) => ({
        ...prev,
        totaloutbound: ttloutbound,
        outboundcost: outbpmt,
        freightpmt: frtpmt,
        materialvalue: matvalue,
        totalduty: ttlduty,
        dutyfee: dtyfee,
        harborfee: hbrfee,
        merchprocfee: mrcfee,
        totalcentryfee: ttlcentryfee,
        importcustomsfee: centrypmt,
        totalinbound: ttlinbound,
        inboundcost: inbpmt,
        totalcost: ttlcost,
        insurancecost: inscost,
        interestcost: intercost,
        totallandedcost: paii,
        profitpmt: profit,
        profittotal: totalprofit,
        turnover: trnover,
        margin: mrgin,
        netback: ntback,
        storagecost: stgcost,
      }));
    };
    docalcs();
  }, [
    USQSData.outdrayage,
    USQSData.loading,
    USQSData.outcollectcharges,
    USQSData.outboundothers,
    USQSData.outpayload,
    USQSData.oceanfreight,
    USQSData.oceanpayload,
    USQSData.quantity,
    USQSData.materialcost,
    USQSData.generaldutyimp,
    USQSData.additionaldutyimp,
    USQSData.harborfeepct,
    USQSData.merchprocfeepct,
    USQSData.flatfee,
    USQSData.tsca,
    USQSData.isf,
    USQSData.inpayload,
    USQSData.indrayage,
    USQSData.unloading,
    USQSData.incollectcharges,
    USQSData.inboundothers,
    USQSData.pcomission,
    USQSData.pfinancecost,
    USQSData.scomission,
    USQSData.sfinancecost,
    USQSData.inspectioncost,
    USQSData.legal,
    USQSData.pallets,
    USQSData.other,
    USQSData.insurancerate,
    USQSData.insurancefactor,
    USQSData.pinterest,
    USQSData.sinterest,
    ppaymenttermDataUSQS,
    spaymenttermDataUSQS,
    USQSData.salesprice,
    USQSData.whexit,
    USQSData.numpallets,
    whdata?.whfixedcost,
    whdata?.whvarcost,
    whdata?.quantity,
    whdata?.numpallets,
    whdata?.whaccrualperiod,
    whdata?.whentrydate,
  ]);
};

export default useCalculations;
